import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/api';
import { analyticsEnabled } from '../lib/analytics';
import { fetchJson } from '../lib/apiFetchJson';
import { useClerk } from '@clerk/clerk-react';
import { UserControlsSchema, type UserControls } from '@shared/me/wire';
import { logger } from '../lib/logger';
import { ApiProblemError } from '../lib/apiFetchJson';
import { useAuthPrincipal } from '../hooks/useAuthPrincipal';
import { useSyncEffectiveIdentity } from '../hooks/useSyncEffectiveIdentity';
import { useImpersonateWriteModeStore } from '../lib/impersonate-write-mode';


/** Pre-load placeholder only. Never the answer to a failed /api/me: the provider blocks on error instead. */
export const defaultUserControls: UserControls = {
  isAdmin: false,
  isMarketingUser: false,
  canImpersonate: false,
  canStartDemo: false,
  isManager: false,
  userType: null,
  flowRole: null,
  realUserType: null,
  realFlowRole: null,
  viewingAs: false,
  email: null,
  impersonation: null,
  userId: null,
  roles: [],
  dialSeats: { parallel: false },
};

const UserControlsContext = createContext<UserControls>(defaultUserControls);

/**
 * Whether /api/me has delivered controls at least once.
 * Defaults to false — consumers (e.g. RequireRole) must treat "not loaded"
 * as "don't know yet", not "no roles", to avoid a redirect flash while the
 * query is in flight. On error the provider renders a blocking error screen
 * instead of its children, so this flag is only ever true alongside real data.
 */
const UserControlsLoadedContext = createContext<boolean>(false);

/**
 * Fetches /api/me once an auth principal is known and provides it via context.
 *
 * `RequireAuth` is a route-level gate: it does not mount this provider until
 * `useAuthPrincipal()` reports `ready`, so by the time this component runs,
 * a Bearer is already available. `principalKey` is still read here for the
 * query's cache key, so an in-place session/user switch refetches with the
 * new identity instead of serving stale controls.
 */
export function UserControlsProvider({ children }: { children: React.ReactNode }) {
  const { apiFetch } = useApi();
  const { principalKey } = useAuthPrincipal();

  const {
    data,
    isError,
    isFetching,
    error,
    refetch,
  } = useQuery<UserControls>({
    queryKey: ['/api/me', principalKey ?? 'none'],
    queryFn: ({ signal }) => fetchJson(apiFetch, UserControlsSchema, '/api/me', { signal }),
    staleTime: Infinity,
    gcTime: Infinity,
    // /api/me is the first query the authenticated shell fires (mounted at
    // the top of UserControlsProvider), so it's the first to 409
    // slack_user_not_found for a Clerk-ready-but-unmapped user. That 409 is
    // already handled — `lib/api.ts`'s apiFetch navigates the whole page to
    // /sign-in?reason=slack_user_not_found — so this query's own error
    // surfacing here would just be a redundant (and, on retry, repeated)
    // toast on top of a navigation that's already in flight.
    meta: { suppressErrorToast: true },
  });
  const controls = data ?? defaultUserControls;
  // A retry flips the query back to pending (isError false) with no data; the
  // screen must stay up for it instead of releasing the app on defaults.
  const [failedOnce, setFailedOnce] = useState(false);
  useEffect(() => {
    if (isError) setFailedOnce(true);
    else if (data !== undefined) setFailedOnce(false);
  }, [isError, data]);
  const isLoaded = data !== undefined;

  useSyncEffectiveIdentity(controls);

  // Layout effect, after the identity sync above (which resets the store on a
  // real identity change), so background writes fired from child effects in
  // the same commit already see it. The identity deps re-publish after that reset.
  // A data gap (a queryKey change from a principalKey flap, refetch pending)
  // publishes nothing: null there reads as "not impersonating" while writes
  // may be unlocked, so background writes would go out as the impersonated
  // rep. Only the identity-scoped reset clears a known value.
  const impersonating = data ? data.impersonation?.active === true : null;
  const actingAsSlackId = controls.impersonation?.actingAsSlackId ?? null;
  useLayoutEffect(() => {
    if (impersonating === null) return;
    useImpersonateWriteModeStore.setState({ impersonating });
  }, [impersonating, controls.userId, actingAsSlackId]);

  useEffect(() => {
    if (controls.userId == null) return;
    // Same gate as track/page — never identify (write traits) into the prod
    // Segment source from a non-prod session unless explicitly opted in.
    if (!analyticsEnabled()) return;
    try {
      window.analytics?.identify(controls.userId.toString(), {
        email: controls.email ?? undefined,
        // Real team, not the lens: client identify traits must agree with
        // server-emitted events (src/services/segment.ts uses the DB user
        // type), so a lensed session never mis-tags a rep's team. See
        // "Analytics identity stays real" in the role-view-switcher plan.
        user_type: controls.realUserType ?? undefined,
        flow_role: controls.realFlowRole ?? undefined,
        is_impersonated: controls.impersonation?.active ?? false,
        impersonator_id: controls.impersonation?.impersonatorId ?? undefined,
        viewing_as: controls.viewingAs,
      });
    } catch {}
  }, [
    controls.userId,
    controls.email,
    controls.realUserType,
    controls.realFlowRole,
    controls.viewingAs,
    controls.impersonation,
  ]);

  // Fail closed and visibly. A failed or malformed /api/me used to fall back
  // to defaultUserControls, which silently reads as "not admin, not
  // impersonating" while the server may still be impersonating. Block instead.
  if (data === undefined && (isError || failedOnce)) {
    return <UserControlsErrorScreen error={error} retrying={isFetching} onRetry={() => void refetch()} />;
  }

  return (
    <UserControlsContext.Provider value={controls}>
      <UserControlsLoadedContext.Provider value={isLoaded}>
        {children}
      </UserControlsLoadedContext.Provider>
    </UserControlsContext.Provider>
  );
}

function UserControlsErrorScreen({
  error,
  retrying,
  onRetry,
}: {
  error: unknown;
  retrying: boolean;
  onRetry: () => void;
}) {
  const { signOut } = useClerk();
  // fetchJson already warns (with issues) on malformed bodies; log the rest once.
  useEffect(() => {
    if (error instanceof ApiProblemError && error.kind === 'malformed') return;
    logger.warn(
      { kind: error instanceof ApiProblemError ? error.kind : 'unknown', status: error instanceof ApiProblemError ? error.status : undefined },
      '[user-controls] /api/me failed',
    );
  }, [error]);
  return (
    <div role="alert" className="flex h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-foreground">Terra could not load your account details.</p>
      <p className="text-muted-foreground">
        Your permissions and any active impersonation could not be confirmed, so the app is paused.
      </p>
      <button
        type="button"
        className="rounded-md border px-3 py-1.5 text-foreground"
        disabled={retrying}
        onClick={onRetry}
      >
        {retrying ? 'Trying again…' : 'Try again'}
      </button>
      <button type="button" className="text-muted-foreground underline" onClick={() => void signOut()}>
        Sign out
      </button>
    </div>
  );
}

export function useUserControlsContext(): UserControls {
  return useContext(UserControlsContext);
}

/**
 * True once /api/me has delivered controls. False while the query is in
 * flight — consumers must not treat "false" as "no permissions", only as
 * "unknown yet".
 */
export function useUserControlsLoaded(): boolean {
  return useContext(UserControlsLoadedContext);
}
